import { defineComponent as _defineComponent } from 'vue'
import BCXConfirm from '@/components/molecules/BCXConfirm.vue';
import { computed, onUnmounted, ref } from 'vue';

import MarvinLookDown from '@/components/svg/marvin/marvin_look_down_straight.svg?inline';

import useI18n from '@/mixins/useI18n';
import useBCXConfirm from '@/mixins/useBCXConfirm';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import { ConfirmActionEventType, TimeSlotDetail } from '@/models/MeetingScheduler';
import useMeetingScheduler from '@/components/meeting-scheduler/composables/useMeetingScheduler';
import BCXRadio from '@/components/molecules/forms/BCXRadio.vue';
import BCXForm from '@/components/molecules/forms/BCXForm.vue';
import { BcxFormField } from '@/models/BcxForm';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import useUser from '@/mixins/useUser';

interface DialogConfig {
  okType: 'warning' | 'ok' | 'primary';
  yesLabel: string;
  noLabel: string;
  translationPrefix: string;
}

interface DialogForm {
  radioSelection: 'first' | 'second';
  message: string;
  selectedSlot: TimeSlotDetail | null;
}

interface EventProps {
  selectedSlot?: TimeSlotDetail | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DialogConfirmAction',
  setup(__props) {

const { onConfirm, showConfirm, ask } = useBCXConfirm();
const { getFormattedTimeRange, getFormattedDate } = useFormattedDateTime();

const {
  events,
  meetingDetail,
  fetchAcceptMeeting,
  fetchMeetingDetail,
  refreshCalendar,
  selectedItem,
  selectedDate,
  fetchCancelMeeting,
  resetState,
  startDraft
} = useMeetingScheduler();
const { t } = useI18n();
const { userId } = useUser('self');

const partnerUserId = computed(() => {
  const detail = meetingDetail.value;
  if (!detail) return '';
  return detail.creator.userId === userId.value ? detail.participant.userId : detail.creator.userId;
});

const DIALOG_CONFIGS: Record<ConfirmActionEventType, DialogConfig> = {
  accept: {
    okType: 'ok', yesLabel: t('buttons.yes'), translationPrefix: 'meeting.dialogs.accept', noLabel: t('buttons.cancel')
  },
  reject: {
    okType: 'primary', yesLabel: t('buttons.reject'), translationPrefix: 'meeting.dialogs.reject', noLabel: t('buttons.cancel')
  },
  decline: {
    okType: 'primary', yesLabel: t('buttons.decline'), translationPrefix: 'meeting.dialogs.decline', noLabel: t('buttons.cancel')
  },
  delete: {
    okType: 'primary', yesLabel: t('buttons.delete'), translationPrefix: 'meeting.dialogs.delete', noLabel: t('buttons.cancel')
  }
};

const currentEvent = ref<ConfirmActionEventType>('delete');
const dialogConfig = computed<DialogConfig>(() => DIALOG_CONFIGS[currentEvent.value]);

const form = ref<DialogForm>({
  radioSelection: 'first',
  message: '',
  selectedSlot: null
});

const fields = computed<BcxFormField[]>(() => [
  {
    name: 'message',
    width: '100%',
    label: t(`${dialogConfig.value.translationPrefix}.message`),
    type: 'textarea',
    textareaOptions: { height: 64, enlargedHeight: 150, maxLength: 500 }
  }
]);

async function refreshAfterAction() {
  if (selectedItem.value?.meetingId) await fetchMeetingDetail(selectedItem.value.meetingId);
  await refreshCalendar();
}

async function acceptMeeting() {
  if (!form.value.selectedSlot || !meetingDetail.value) return;
  await fetchAcceptMeeting(meetingDetail.value.meetingId, {
    timeSlotId: form.value.selectedSlot.timeSlotId,
    message: form.value.message
  });

  selectedDate.value = new Date(form.value.selectedSlot.start);
  await refreshAfterAction();
}

async function cancelMeeting() {
  if (!meetingDetail.value) return;
  await fetchCancelMeeting(meetingDetail.value.meetingId, { message: form.value.message });
  resetState();
  await refreshCalendar();
}

function openProposalIfRequested(partnerId: string) {
  if (form.value.radioSelection !== 'second') return;
  startDraft(partnerId);
}

const off = events.on(async (evt: ConfirmActionEventType, eventProps?: EventProps) => {
  form.value = {
    radioSelection: 'first',
    message: '',
    selectedSlot: eventProps?.selectedSlot ?? null
  };
  currentEvent.value = evt;

  if (!(await ask())) return;

  if (evt === 'accept') {
    await acceptMeeting();
  } else {
    const partnerId = partnerUserId.value;
    await cancelMeeting();
    openProposalIfRequested(partnerId);
  }
});

onUnmounted(off);

return { __sfc: true,onConfirm, showConfirm, ask, getFormattedTimeRange, getFormattedDate, events, meetingDetail, fetchAcceptMeeting, fetchMeetingDetail, refreshCalendar, selectedItem, selectedDate, fetchCancelMeeting, resetState, startDraft, t, userId, partnerUserId, DIALOG_CONFIGS, currentEvent, dialogConfig, form, fields, refreshAfterAction, acceptMeeting, cancelMeeting, openProposalIfRequested, off, BCXConfirm, MarvinLookDown, BCXMarkdown, BCXRadio, BCXForm }
}

})